import { TrackProperties } from '../../services/analyticsService';
import { coverPageFields, finalFieldsHeaders } from '../file-upload/Payapp.util';

/** Number of cover page fields with no value (empty or whitespace); 0 counts as filled. */
export const countBlankCoverFields = (values: Record<string, string | number> | null | undefined): number =>
  coverPageFields.filter(field => String(values?.[field] ?? '').trim() === '').length;

const isMappedHeader = (header?: string): header is string => !!header && finalFieldsHeaders.some(f => f.headerName === header);

/** Columns whose field Fix Headers set or changed (ignores renames between two non-field labels). */
export const getAiFixedHeaderColumns = (before: Record<string, string>, after: Record<string, string>): string[] =>
  Object.keys(after).filter(col => before[col] !== after[col] && (isMappedHeader(before[col]) || isMappedHeader(after[col])));

/** Step 2 header changes: each PDF column's mapping at the start (after Fix Headers) compared with now. */
export const getHeaderMetrics = (
  original: Record<string, string>,
  current: Record<string, string>,
  aiFixedColumns: string[] = [],
): TrackProperties => {
  const columns = new Set([...Object.keys(original), ...Object.keys(current)].filter(col => col !== 'id'));
  let unassigned = 0;
  let reassigned = 0;
  let newlyAssigned = 0;
  columns.forEach(col => {
    const before = isMappedHeader(original[col]) ? original[col] : null;
    const after = isMappedHeader(current[col]) ? current[col] : null;
    if (before && !after) unassigned += 1;
    else if (before && after && before !== after) reassigned += 1;
    else if (!before && after) newlyAssigned += 1;
  });
  return {
    headers_total: Object.keys(original).filter(col => col !== 'id').length,
    headers_unassigned: unassigned,
    headers_reassigned: reassigned,
    headers_newly_assigned: newlyAssigned,
    headers_fixed_by_ai: aiFixedColumns.length,
  };
};

/**
 * Step 2 cell edits: extracted cells (mapped columns, rows that existed at the start) whose value differs from the baseline,
 * merged with keys saved from earlier sessions. Fix OCR changes are folded into the baseline, so they are counted separately.
 */
export const getCellEditState = (state: any) => {
  const rows: any[] = state.tableRows ?? [];
  const baselineById = new Map<string, any>((state.cellBaselineRows ?? []).map((row: any) => [row.id, row]));
  const mappedColumns = Object.keys(state.fieldHeadersMapping ?? {}).filter(col => isMappedHeader(state.fieldHeadersMapping[col]));
  const currentIds = new Set(rows.map(row => row.id));
  const stillExists = (key: string) => currentIds.has(key.split(':')[0]);

  const editedKeys = new Set<string>((state.priorEditedCellKeys ?? []).filter(stillExists));
  let cellsTotal = 0;
  rows.forEach(row => {
    const base = baselineById.get(row.id);
    if (!base) return;
    mappedColumns.forEach(col => {
      if (!(col in base)) return;
      cellsTotal += 1;
      if (String(row[col] ?? '').trim() !== String(base[col] ?? '').trim()) editedKeys.add(`${row.id}:${col}`);
    });
  });
  const ocrFixedKeys = new Set<string>(
    [...(state.priorOcrFixedCellKeys ?? []), ...Array.from<string>(state.ocrFixedFields ?? [])].filter(stillExists),
  );

  return { editedKeys: Array.from(editedKeys), ocrFixedKeys: Array.from(ocrFixedKeys), cellsTotal };
};

/** Step 2 subtotals: all are marked by hand; a set `subtotalRange` means the user edited its contents. */
export const getSubtotalMetrics = (rows: any[]): TrackProperties => {
  const subtotals = (rows ?? []).filter(row => row.isSubtotal === true);
  return {
    subtotals_total: subtotals.length,
    subtotals_range_edited: subtotals.filter(row => row.subtotalRange != null).length,
  };
};

/**
 * Context properties attached to every payapp workflow event so the Mixpanel
 * payapp report can build one row per payapp (payapp_id) and per sitting (payapp_session_id).
 */
export const getPayappAnalyticsProps = (state: any, sessionId: string): TrackProperties => ({
  payapp_id: state.projectDraw?.payappId ?? state.originalProjectDraw?.payappId ?? '',
  payapp_session_id: sessionId,
  project_id: String(state.projectId ?? ''),
  project_number: state.projectNumber ?? '',
  project_name: state.projectName ?? '',
  application_number: state.coverPageValues?.['Application Number'] || state.projectDraw?.projectDrawSummary?.applicationNumber || '',
  draw_name: state.projectDraw?.projectDrawSummary?.name ?? '',
});
